import { useMemo, useRef } from "react"
import type { FC, ReactElement, RefObject } from "react"

import { type Variants as TMotionVariants, m, useReducedMotion } from "framer-motion"
import isEmpty from "lodash-es/isEmpty"
import isString from "lodash-es/isString"
import isUndefined from "lodash-es/isUndefined"
import map from "lodash-es/map"
import { useUpdateEffect } from "react-use"

import {
    Button,
    EButtonVariant,
    ETypographyAlignment,
    ETypographyColor,
    ETypographyFamily,
    ETypographySize,
    ETypographyTag,
    ETypographyWeight,
    ErrorHint,
    Typography
} from "@/3514/components"
import { type TScreenBreakpoints, useScreenBreakpoints } from "@/3514/hooks"
import { buildCssClassName } from "@/3514/utils"
import { type IUseTranslation, useTranslation } from "@/hooks"

import { FreeResponse as Field } from "./free-response.component"
import { useFreeResponsesContext as useContext } from "./free-responses.context"
import { freeResponsesStyles as classes } from "./free-responses.styles"
import type { TFreeResponsesConfig as TConfig, TFreeResponsesQuestion as TQuestion } from "./free-responses.types"

const FreeResponses: FC<Partial<TConfig>> = ({ wrapperClassName }: Partial<TConfig>): ReactElement => {
    const isMotionReduced: boolean = useReducedMotion()

    const containerMotionVariants: TMotionVariants = useMemo(
        (): TMotionVariants => ({
            hidden: { opacity: 0 },
            visible: {
                opacity: 1,
                transition: { staggerChildren: isMotionReduced ? 0 : 0.1, delayChildren: isMotionReduced ? 0 : 0.15 }
            }
        }),
        [isMotionReduced]
    )

    const itemMotionVariants: TMotionVariants = useMemo(
        (): TMotionVariants => ({
            hidden: { opacity: 0, y: 20 },
            visible: { opacity: 1, y: 0, transition: { duration: isMotionReduced ? 0 : 0.3, ease: "easeOut" } }
        }),
        [isMotionReduced]
    )

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const { t }: IUseTranslation = useTranslation()

    const errorRef: RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null)

    const {
        theme,
        title,
        isError,
        questionList: fields,
        withManualSubmit,
        submitButtonText,
        form: { isValid, isSubmitting, handleSubmit }
    }: ReturnType<typeof useContext> = useContext()

    useUpdateEffect((): void => {
        if (isError && withManualSubmit) errorRef.current?.scrollIntoView({ behavior: "smooth" })
    }, [isError, withManualSubmit])

    return (
        <section className={buildCssClassName(classes.section({ theme }), { [wrapperClassName]: wrapperClassName })}>
            {isString(title) && (
                <Typography
                    text={title}
                    size={breakpoints.xs || breakpoints.sm ? ETypographySize.Heading2 : ETypographySize.Heading1}
                    family={ETypographyFamily.DmSerif}
                    alignment={ETypographyAlignment.Center}
                    tag={ETypographyTag.Heading1}
                    color={ETypographyColor.Black}
                    weight={ETypographyWeight.Medium}
                />
            )}

            <form onSubmit={handleSubmit} className={classes.form()}>
                {!isEmpty(fields) && (
                    <m.div
                        className={classes.fieldsContainer()}
                        variants={containerMotionVariants}
                        initial="hidden"
                        animate="visible"
                    >
                        {map<TQuestion, ReactElement>(
                            fields,
                            (field: TQuestion, idx: number): ReactElement => (
                                <m.div key={`r-${field.initialState.id}`} variants={itemMotionVariants}>
                                    <Field index={idx} />
                                </m.div>
                            )
                        )}
                    </m.div>
                )}

                {withManualSubmit && (
                    <div className={classes.buttonContainer()}>
                        <ErrorHint ref={errorRef} isVisible={isError} />

                        <Button
                            type="submit"
                            minWidth="fit-content"
                            variant={EButtonVariant.Outlined}
                            isBusy={isSubmitting}
                            isDisabled={!isValid || isError}
                        >
                            {isUndefined(submitButtonText) ? t("common.submit") : submitButtonText}
                        </Button>
                    </div>
                )}
            </form>
        </section>
    )
}

export { FreeResponses }
